import { useEffect, useRef, useState } from 'react'

import styles from './SelectMenu.module.css'

export interface SelectMenuOption {
  value: string
  label: string
}

/**
 * 커스텀 셀렉트 (드롭다운). 네이티브 <select>의 OS 렌더 드롭다운(모바일에서 어두운 시트)을
 * 피하려고 버튼 + 팝오버 목록으로 직접 그린다. 옵션이 많은 필터(상태 등)에 쓴다.
 * 접근성: aria-haspopup/expanded + role=listbox/option, 바깥 클릭·Esc로 닫힘.
 */
export function SelectMenu({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: string
  options: SelectMenuOption[]
  onChange: (value: string) => void
  ariaLabel: string
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const current = options.find((o) => o.value === value) ?? options[0]

  useEffect(() => {
    if (!open) return
    function onDocDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        type="button"
        className={styles.trigger}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={styles.label}>{current?.label ?? ''}</span>
        <svg className={styles.chevron} data-open={open} viewBox="0 0 20 20" aria-hidden="true">
          <path
            d="M5.5 8l4.5 4.5L14.5 8"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open ? (
        <ul className={styles.menu} role="listbox" aria-label={ariaLabel}>
          {options.map((o) => {
            const selected = o.value === value
            return (
              <li key={o.value || '__all'}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={selected ? `${styles.option} ${styles.optionOn}` : styles.option}
                  onClick={() => {
                    onChange(o.value)
                    setOpen(false)
                  }}
                >
                  <span>{o.label}</span>
                  {selected ? (
                    <span className={styles.check} aria-hidden="true">
                      ✓
                    </span>
                  ) : null}
                </button>
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}
